import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./NewPassword.css";
import logo from "../assets/logo.png";
import setNewPasswordImg from "../assets/set-new-password.png";
import axios from "axios";
import { FaEye, FaEyeSlash } from "react-icons/fa";

/* ================= WHATSAPP FLOAT COMPONENT ================= */
const WhatsAppFloat = ({ 
  phoneNumber = "15485825756", 
  message = "Hello! I need urgent support from safepalPay customer service for password reset.",
  position = "right",
  bottom = "30px",
  right = "30px",
  left = "auto",
  size = "54px",
  iconSize = "28px",
  pulseEffect = true,
  className = "",
  style = {}
}) => {
  const formattedNumber = phoneNumber.replace(/[^\d]/g, '');
  const whatsappUrl = `https://wa.me/${formattedNumber}?text=${encodeURIComponent(message)}`;
  
  const positionStyles = position === "left" 
    ? { left: left || "20px", right: "auto" }
    : { right: right || "20px", left: "auto" };

  const combinedStyles = {
    position: 'fixed',
    bottom: bottom,
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: '#25d366',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    zIndex: 10000,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textDecoration: 'none',
    ...positionStyles,
    ...style
  };

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className={`whatsapp-float-unique ${pulseEffect ? 'pulse-unique' : ''} ${className}`}
      style={combinedStyles}
      aria-label="Chat on WhatsApp"
      title="Chat on WhatsApp for password reset assistance"
    >
      <svg 
        width={iconSize} 
        height={iconSize} 
        viewBox="0 0 32 32"
        fill="white"
      >
        <path d="M16.001 3C9.373 3 4 8.373 4 15.001c0 2.645.867 5.084 2.329 7.058L5 29l7.142-1.875A11.96 11.96 0 0016 27c6.627 0 12-5.373 12-11.999C28 8.373 22.627 3 16.001 3zm0 21.999c-1.924 0-3.715-.57-5.219-1.548l-.372-.23-4.242 1.114 1.133-4.135-.242-.384A9.957 9.957 0 016 15.001C6 9.486 10.486 5 16.001 5 21.515 5 26 9.486 26 15.001c0 5.514-4.485 9.998-9.999 9.998zm5.548-7.557c-.303-.151-1.793-.886-2.071-.987-.279-.101-.483-.151-.687.151-.202.302-.787.987-.965 1.19-.177.202-.354.227-.656.075-.303-.151-1.279-.471-2.435-1.5-.9-.802-1.508-1.792-1.686-2.094-.177-.303-.019-.467.133-.619.136-.135.303-.354.455-.531.152-.177.202-.303.303-.505.101-.202.051-.379-.025-.531-.076-.151-.687-1.656-.941-2.269-.247-.592-.498-.512-.687-.521-.177-.008-.379-.01-.582-.01-.202 0-.531.075-.808.379-.278.303-1.06 1.035-1.06 2.524 0 1.488 1.085 2.926 1.237 3.128.151.202 2.14 3.27 5.183 4.588.724.312 1.288.498 1.728.637.726.231 1.387.198 1.911.12.583-.087 1.793-.733 2.047-1.44.253-.707.253-1.313.177-1.44-.076-.126-.278-.198-.581-.354z" />
      </svg>
    </a>
  );
};

const NewPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Animated popup state
  const [popup, setPopup] = useState({
    show: false,
    message: "",
    success: false,
  });

  // Crisp Chat Integration
  useEffect(() => {
    window.$crisp = window.$crisp || [];
    window.CRISP_WEBSITE_ID = "b5635951-f13a-4b92-95d7-c1e3666f3abf";

    window.$crisp.push(["do", "chat:show"]);

    if (!document.querySelector('script[src="https://client.crisp.chat/l.js"]')) {
      const script = document.createElement("script");
      script.src = "https://client.crisp.chat/l.js";
      script.async = true;
      document.head.appendChild(script);
    }

    return () => {
      if (window.$crisp) {
        window.$crisp.push(["do", "chat:hide"]);
      }
    };
  }, []);

  const email = location.state?.email || localStorage.getItem("resetEmail");
  const resetCode = location.state?.resetCode || localStorage.getItem("verifiedResetCode");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.newPassword || formData.newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    if (!email || !resetCode) {
      setPopup({
        show: true,
        message: "Session expired. Please restart reset process.",
        success: false,
      });
      return;
    }

    setIsLoading(true);

    try {
      const res = await axios.post(
        "https://backend-safepal.onrender.com/api/auth/reset-password",
        {
          email,
          resetCode,
          newPassword: formData.newPassword,
        }
      );

      if (res.data.success) {
        setPopup({
          show: true,
          message: "Password reset successfully!",
          success: true,
        });

        localStorage.removeItem("resetEmail");
        localStorage.removeItem("verifiedResetCode");

        setTimeout(() => {
          navigate("/passwordresetsuccess");
        }, 1500);
      }
    } catch (err) {
      setPopup({
        show: true,
        message: err.response?.data?.error || "Failed to reset password",
        success: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="np-new-password-container-unique">
        <div className="np-new-password-card-unique">
          <div className="np-new-password-logo-unique">
            <img src={logo} alt="SafepalPay" />
          </div>

          <h2 className="np-new-password-title-unique">SET A NEW PASSWORD</h2>

          {/* New Password Illustration */}
          <div className="np-new-password-support-image-unique">
            <img src={setNewPasswordImg} alt="Set New Password Illustration" />
          </div>

          <p className="np-new-password-subtitle-unique">
            Create a new password. Ensure it differs from previous ones.
          </p>

          <label className="np-new-password-input-label-unique">Enter Password</label>
          <div className="np-new-password-input-box-unique">
            <input
              type={showPassword ? "text" : "password"}
              name="newPassword"
              placeholder="Enter Password"
              value={formData.newPassword}
              onChange={handleChange}
              disabled={isLoading}
            />
            <span 
              className="np-password-toggle-icon-unique"
              onClick={() => !isLoading && setShowPassword(!showPassword)}
              style={{ cursor: isLoading ? "not-allowed" : "pointer", opacity: isLoading ? 0.5 : 1 }}
            >
              {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
            </span>
          </div>
          {errors.newPassword && (
            <div className="np-new-password-input-error-unique">{errors.newPassword}</div>
          )}

          <label className="np-new-password-input-label-unique">Re-enter Password</label>
          <div className="np-new-password-input-box-unique">
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder="Re-enter Password"
              value={formData.confirmPassword}
              onChange={handleChange}
              disabled={isLoading}
            />
            <span
              className="np-password-toggle-icon-unique"
              onClick={() => !isLoading && setShowConfirmPassword(!showConfirmPassword)}
              style={{ cursor: isLoading ? "not-allowed" : "pointer", opacity: isLoading ? 0.5 : 1 }}
            >
              {showConfirmPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
            </span>
          </div>
          {errors.confirmPassword && (
            <div className="np-new-password-input-error-unique">
              {errors.confirmPassword}
            </div>
          )}

          <button
            className="np-new-password-update-btn-unique"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? "Updating..." : "Update Password"}
          </button>
        </div>

        {/* ANIMATED POPUP */}
        {popup.show && (
          <div className="np-new-password-popup-overlay-unique">
            <div className="np-new-password-popup-card-unique">
              <div
                className={`np-new-password-icon-box-unique ${
                  popup.success ? "np-new-password-success-unique" : "np-new-password-error-unique"
                }`}
              >
                <svg viewBox="0 0 100 100" className="np-new-password-icon-unique">
                  <circle cx="50" cy="50" r="45" className="np-new-password-circle-unique" />
                  <path
                    className="np-new-password-path-unique"
                    d={
                      popup.success
                        ? "M30 52 L45 65 L70 38"
                        : "M35 35 L65 65 M65 35 L35 65"
                    }
                  />
                </svg>
              </div>

              <p className="np-new-password-popup-text-unique">{popup.message}</p>

              <button
                className="np-new-password-ok-btn-unique"
                onClick={() => setPopup({ ...popup, show: false })}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>

      {/* WhatsApp Float Button - For Password Reset Assistance */}
      <WhatsAppFloat 
        phoneNumber="15485825756"
        message="Hello! I need urgent support from safepalPay customer service for password reset."
        position="right"
        bottom="30px"
        right="30px"
        pulseEffect={true}
        size="54px"
        iconSize="28px"
      />
    </>
  );
};

export default NewPassword;